import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  City,
  Customer,
  NetsapiensDomain,
  OnBoardingRecord,
  State,
} from '@voice/utils';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { GeographyService } from '../../../services/geography.service';
import { NotificationsService } from '../../../services/notifications.service';
import {
  SelectButtonChangeEvent,
  SelectButtonModule,
} from 'primeng/selectbutton';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { WizardButtonsComponent } from './wizard-buttons.component';
import { ClientOnboardingService } from '../../../services/client-onboarding.service';
import { Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { DomainsService } from '../../../services/domains.service';
import { downloadCSV } from '../../../utils/file-manager';
import {
  BillingService,
  RevioCustomer,
} from '../../../services/billing.service';
import { firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'app-client-onboarding-customer',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    DropdownModule,
    SelectButtonModule,
    NgClass,
    NgIf,
    NgFor,
    WizardButtonsComponent,
    ConfirmDialogModule,
    DialogModule,
    ButtonModule,
  ],
  templateUrl: './customer-form.component.html',
})
export class CustomerFormComponent implements OnInit {
  constructor(
    private readonly router: Router,
    private readonly clientOnboardingService: ClientOnboardingService,
    private readonly geographyService: GeographyService,
    private readonly notificationService: NotificationsService,
    private readonly confirmationService: ConfirmationService,
    private readonly domainService: DomainsService,
    private readonly billingService: BillingService
  ) {}

  @Input()
  onBoardingId?: number;

  @Input()
  domain?: string;

  @Input()
  customer!: Customer;

  states!: State[];
  cities!: City[];
  domains!: NetsapiensDomain[];

  selectedState?: State;
  selectedCity?: City;

  existingCustomers: RevioCustomer[] = [];
  checkingCustomer = false;
  customerExistsWarning = false;
  showCustomerExistsDialog = false;

  customerClassValues = [
    { label: 'Business', value: 'Business' },
    { label: 'Residential', value: 'Residential' },
  ];

  billingMethodValues = [
    { label: 'None', value: 'NONE' },
    { label: 'Print only', value: 'PRINT_ONLY' },
    { label: 'Email only', value: 'EMAIL_ONLY' },
    { label: 'Both', value: 'BOTH' },
  ];

  private static readonly BILLING_METHODS_REQUIRING_EMAIL = [
    'EMAIL_ONLY',
    'BOTH',
  ];

  customerFormGroup!: FormGroup;

  @Output()
  customerSaved: EventEmitter<OnBoardingRecord> =
    new EventEmitter<OnBoardingRecord>();

  ngOnInit(): void {
    if (!this.customer.customerClass) {
      this.customer.customerClass = this.customerClassValues[0].value;
    }
    if (!this.customer.billingMethod) {
      this.customer.billingMethod = this.billingMethodValues[0].value;
    }
    this.fetchStates().then(() => {
      if (this.customer.state && this.customer.city) {
        this.selectedState = this.states.find(
          (state) => state.code === this.customer.state
        );
        this.fetchCities().then(() => {
          this.selectedCity = this.cities.find(
            (city) => city.name === this.customer.city
          );
          this.initializeFormGroup();
        });
      } else {
        this.initializeFormGroup();
      }
    });
    if (!this.onBoardingId) {
      this.domainService.getDomains().subscribe({
        next: (data) => (this.domains = data),
        error: (err) => this.notificationService.error(err),
      });
    }
  }

  onStateChange(event: DropdownChangeEvent) {
    this.selectedState = event.value;
    this.fetchCities().then();
  }

  onCityChange(event: DropdownChangeEvent) {
    this.selectedCity = event.value;
  }

  isControlInvalid(controlName: string): boolean | undefined {
    return (
      this.customerFormGroup.get(controlName)?.invalid &&
      this.customerFormGroup.get(controlName)?.touched
    );
  }

  isEmailRequired(): boolean {
    const billingMethod =
      this.customerFormGroup?.get('billingMethod')?.value ??
      this.customer.billingMethod;
    return CustomerFormComponent.BILLING_METHODS_REQUIRING_EMAIL.includes(
      billingMethod
    );
  }

  handleCommonButtonClicked(event: string) {
    if (event === 'next') {
      this.saveCustomer();
    } else {
      this.returnToList();
    }
  }

  onCustomerClassChange(event: SelectButtonChangeEvent) {
    this.customer.customerClass = event.value;
  }

  onBillingMethodChange(event: SelectButtonChangeEvent) {
    this.customer.billingMethod = event.value;
    this.updateEmailValidators();
  }

  private updateEmailValidators(): void {
    const billingMethod =
      this.customerFormGroup.get('billingMethod')?.value ??
      this.customer.billingMethod;
    const emailControl = this.customerFormGroup.get('email');
    if (!emailControl) return;
    const requiresEmail = CustomerFormComponent.BILLING_METHODS_REQUIRING_EMAIL.includes(
      billingMethod
    );
    if (requiresEmail) {
      emailControl.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl.setValidators([Validators.email]);
    }
    emailControl.updateValueAndValidity();
  }

  onDomainChange(event: SelectButtonChangeEvent) {
    this.domain = event.value;
    this.domainService.getDomainDetails(event.value, true).subscribe({
      next: (data) => {
        if (this.customerFormGroup && data['address']) {
          const address = data['address'];

          this.customerFormGroup.get('name')?.setValue(address['caller-name']);

          this.customerFormGroup
            .get('addressLineOne')
            ?.setValue(address['address-line-1']);
          this.customerFormGroup
            .get('addressLineTwo')
            ?.setValue(address['address-line-2']);

          const zip = address['address-postal-code']?.split('-');
          this.customerFormGroup.get('zipCode')?.setValue(zip[0]);

          const state = address['address-state-province-abbreviation'];
          const city = address['address-city'];

          if (state) {
            this.selectedState = this.states.find((s) => s.code === state);
            this.customerFormGroup.get('state')?.setValue(this.selectedState);
            if (city) {
              this.fetchCities().then(() => {
                this.selectedCity = this.cities.find(
                  (c) => c.name.toUpperCase() === city.toUpperCase()
                );
                this.customerFormGroup.get('city')?.setValue(this.selectedCity);
              });
            }
          }
        }
      },
    });
  }

  private initializeFormGroup() {
    this.customerFormGroup = new FormGroup({
      domain: new FormControl(
        {
          value: this.domain,
          disabled: this.onBoardingId !== undefined && this.domain !== null,
        },
        [Validators.required]
      ),
      name: new FormControl(this.customer?.name, [Validators.required]),
      email: new FormControl(this.customer?.email, [Validators.email]),
      addressLineOne: new FormControl(
        this.customer?.addressLine1,
        Validators.required
      ),
      addressLineTwo: new FormControl(this.customer?.addressLine2 ?? ''),
      state: new FormControl(this.selectedState, Validators.required),
      city: new FormControl(this.selectedCity, Validators.required),
      zipCode: new FormControl(this.customer.zipCode, [
        Validators.required,
        Validators.pattern('[0-9]{5}(?:-[0-9]{4})?'),
      ]),
      customerClass: new FormControl(this.customer.customerClass),
      billingMethod: new FormControl(this.customer.billingMethod),
      masId: new FormControl(this.customer.masId ?? ''),
    });
    this.updateEmailValidators();

    // Watch for name changes and check if customer exists
    this.customerFormGroup
      .get('name')
      ?.valueChanges.pipe(
        debounceTime(500), // Wait 500ms after user stops typing
        distinctUntilChanged(), // Only check if value actually changed
        switchMap((name: string) => {
          this.checkingCustomer = true;
          this.customerExistsWarning = false;
          this.existingCustomers = [];

          if (!name || name.trim().length < 2) {
            this.checkingCustomer = false;
            return of([]);
          }

          return this.billingService.revioSearchCustomersByName(name);
        })
      )
      .subscribe({
        next: (customers) => {
          this.checkingCustomer = false;
          if (customers && Array.isArray(customers) && customers.length > 0) {
            // Create a copy to ensure data persists
            this.existingCustomers = customers.map((c) => ({ ...c }));
            this.customerExistsWarning = true;
            console.log('Found existing customers:', this.existingCustomers);
            // Show dialog when customer is found (only if we have customers)
            // Use setTimeout to ensure the data is set before showing dialog
            setTimeout(() => {
              console.log(
                'Showing dialog with customers:',
                this.existingCustomers
              );
              if (this.existingCustomers && this.existingCustomers.length > 0) {
                this.showCustomerExistsDialog = true;
              }
            }, 100);
          } else {
            this.existingCustomers = [];
            this.customerExistsWarning = false;
            this.showCustomerExistsDialog = false;
          }
        },
        error: (err) => {
          this.checkingCustomer = false;
          console.warn('Error checking customer existence:', err);
        },
      });
  }

  private fetchStates(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.geographyService.getStates().subscribe({
        next: (states: State[]) => {
          this.states = states;
          resolve();
        },
        error: (err) => reject(err),
      });
    });
  }

  private fetchCities(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.selectedState) {
        this.geographyService.getCities(this.selectedState.code).subscribe({
          next: (cities: City[]) => {
            this.cities = cities;
            resolve();
          },
          error: (err) => reject(err),
        });
      }
    });
  }

  private fillCustomerData() {
    this.customer.name = this.customerFormGroup.get('name')?.value;
    this.customer.email = this.customerFormGroup.get('email')?.value;
    this.customer.addressLine1 =
      this.customerFormGroup.get('addressLineOne')?.value;
    this.customer.addressLine2 =
      this.customerFormGroup.get('addressLineTwo')?.value;
    this.customer.zipCode = this.customerFormGroup.get('zipCode')?.value;
    this.customer.customerClass =
      this.customerFormGroup.get('customerClass')?.value;
    this.customer.billingMethod =
      this.customerFormGroup.get('billingMethod')?.value;
    this.customer.state = this.selectedState?.code;
    this.customer.city = this.selectedCity?.name;
    this.customer.masId = this.customerFormGroup.get('masId')?.value || undefined;
  }

  private async saveCustomer(): Promise<void> {
    if (this.customerFormGroup.dirty) {
      this.fillCustomerData();
      if (this.domain) {
        // Check if customer already exists in Rev.io
        if (this.customer.name) {
          try {
            const existingCustomers = await firstValueFrom(
              this.billingService.revioSearchCustomersByName(this.customer.name)
            );

            if (existingCustomers && existingCustomers.length > 0) {
              this.existingCustomers = [...existingCustomers];
              this.showCustomerExistsDialog = true;
              return;
            }
          } catch (error) {
            // If search fails, log but continue with save
            console.warn('Failed to check if customer exists:', error);
          }
        }

        // No existing customer found, proceed with save
        this.performSave();
      } else {
        this.notificationService.error('No domain has been provided').then();
      }
    } else {
      this.customerSaved.emit();
    }
  }

  private performSave(): void {
    if (!this.domain) {
      return;
    }

    this.clientOnboardingService
      .save(this.domain, this.customer, this.onBoardingId)
      .subscribe({
        next: (result) => {
          this.notificationService
            .info('Record saved successfully. ')
            .then(() => this.customerSaved.emit(result as OnBoardingRecord));
        },
        error: (err) => this.notificationService.error(err),
      });
  }

  private returnToList() {
    if (this.customerFormGroup.dirty) {
      this.confirmationService.confirm({
        message: 'There are unsaved changes. Are you sure you want to leave?',
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.router.navigateByUrl('/billing/client-onboarding').then();
        },
        reject: () => {
          console.log('Rejected');
        },
      });
    } else {
      this.router.navigateByUrl('/billing/client-onboarding').then();
    }
  }

  onCustomerExistsDialogClose(): void {
    this.showCustomerExistsDialog = false;
  }

  trackByCustomerId(index: number, customer: RevioCustomer): number {
    return customer.customerId;
  }

  exportCustomer(): void {
    if (!this.customer || !this.customer.name) {
      this.notificationService.error('No customer data to export');
      return;
    }

    const keys = [
      'domain',
      'name',
      'email',
      'addressLine1',
      'addressLine2',
      'city',
      'state',
      'zipCode',
      'customerClass',
      'billingMethod',
      'masId',
    ];

    const exportData = [
      {
        domain: this.domain || '',
        name: this.customer.name || '',
        email: this.customer.email || '',
        addressLine1: this.customer.addressLine1 || '',
        addressLine2: this.customer.addressLine2 || '',
        city: this.customer.city || '',
        state: this.customer.state || '',
        zipCode: this.customer.zipCode || '',
        customerClass: this.customer.customerClass || '',
        billingMethod: this.customer.billingMethod || '',
        masId: this.customer.masId || '',
      },
    ];

    const fileName = `customer_${
      this.customer.name?.replace(/[^a-zA-Z0-9]/g, '_') || 'export'
    }`;
    downloadCSV(fileName, keys, exportData);
  }
}
